// Submissions — customer enquiries from website forms and supplier referrals.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   Page-section markup       : lines 2507–2525  (#page-submissions)
//   Data + render + actions   : lines 6319–6396  (SUB_KEY, seedSubmissions,
//                                                  getSubs/setSubs,
//                                                  renderSubmissions,
//                                                  convertSubmission,
//                                                  replySubmission,
//                                                  archiveSubmission,
//                                                  deleteSubmission,
//                                                  newSubmission /
//                                                    openNewSubmissionModal /
//                                                    closeNewSubmissionModal /
//                                                    submitNewSubmission)
//   Modal builder             : lines 6398–6457  (kosBuildSubModal)
//   CSV export                : line  6458       (exportSubmissionsCsv)
//
// Backend: PostgreSQL `submissions` table (migration 0007), served by
// /api/portal/submissions (modules/portal_submissions.js). This page swapped
// off localStorage on 2026-05-13 as part of the Submissions → PG migration.
// The wizard_submit.js handler also writes into this table so consumer-wizard
// submissions appear here alongside retailer-entered ones.
//
// 2026-05-13 — offline-first layer:
//   Reads/writes go through @tkos/shared-ui's `createRepo` over IndexedDB
//   + a pending-sync queue. PG is still the source of truth; IndexedDB is
//   the offline cache. A sync-status dot in the toolbar shows green
//   (synced), amber (saving), or red (offline). See packages/shared-ui/
//   src/offline for the shared infrastructure used here and by every
//   business-data page that follows (Contacts next).
//
// This module ports source verbatim for markup, inline styles, copy, toast
// strings (including the "(demo)" tags — they are source UX, not stub markers),
// card layout, modal markup/fields, and CSV format. Source's
// `onclick="renderSubmissions()"` / `onclick="convertSubmission('id')"` global
// call pattern is replaced with addEventListener since modular pages forbid
// window globals — the DOM the user sees is identical.
//
// Departures from source:
//  - addEventListener delegation instead of inline onclick / oninput.
//  - localStorage replaced with the offline-first repo (IDB + PG via queue).
//  - Modal lives in body for the lifetime of the page (per source pattern of
//    appending once and toggling display), but we track it in an overlays Set
//    and remove on cleanup so navigating away doesn't leak DOM.
//  - One-off animation <style> tag is also tracked and removed on cleanup.
//  - esc helper inlined locally to avoid leaking globals.
//  - "toast" call in source becomes ctx.showToast.
//  - confirm() is preserved (source uses native window.confirm; same UX).
//  - AbortController cancels in-flight requests on unmount.
//
// No new UI behaviour, no redesign. The sync-status dot in the toolbar is
// the only new element.

import { API_BASE } from "@tkos/api-client";
import {
  confirmDialog,
  createRepo,
  createSyncDot,
  createSyncQueue,
  createSyncStatus,
  iconMarkup,
  maskEmail,
  maskPhone,
  openDb,
  type MiniCrmCustomer,
  type Repo,
  type RepoConflictPayload,
  type SyncStatusController,
  type TkosDb,
} from "@tkos/shared-ui";

import { registerPage } from "../../shell/router";
import { customerCache } from "../../shell/customer-cache";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type Priority = "hot" | "warm" | "cold";
type Status = "new" | "replied" | "quoted";

interface Submission {
  id: string;
  ts: number;
  name: string;
  email: string;
  phone: string;
  message: string;
  source: string;
  priority: Priority;
  status: Status;
  archived: boolean;
  tags: string[];
  /** Server-issued CRM contact id once the submission is linked to a real
   *  CRM record. Empty string until linked. Populated by the wizard intake,
   *  the backfill script, or POST /submissions/:id/link-crm. */
  crmContactId?: string;
}

interface SubmissionsListResp {
  ok?: boolean;
  retailerId?: string;
  submissions?: Submission[];
}

interface SubmissionResp {
  ok?: boolean;
  retailerId?: string;
  submission?: Submission;
}

function isAbortError(err: unknown): boolean {
  return (
    err instanceof DOMException && err.name === "AbortError"
  ) || (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

// Adapt the server's `{ submissions: [...] }` / `{ submission: {...} }`
// envelopes to the bare records the repo expects.
function parseList(json: unknown): Submission[] {
  const j = json as SubmissionsListResp | null;
  return Array.isArray(j?.submissions) ? j!.submissions! : [];
}

function parseOne(json: unknown): Submission | undefined {
  const j = json as SubmissionResp | null;
  return j?.submission ?? undefined;
}

// Raw fetch wrapper for the repo + sync queue. We can't reuse the typed
// api() helper because the queue needs the Response (status code matters
// for retry vs drop). Credentials/JSON header are kept identical to
// api-client so the request is indistinguishable on the wire.
function rawApi(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
}

// ── HTML escape (verbatim port of source line 6314 — version closest to subs)

function esc(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Page-section markup (verbatim port of source lines 2508–2524) ───────────

const PAGE_MARKUP = `
<div class="page-section" id="page-submissions">
  <div style="padding:18px 22px 8px">
    <h2 style="font-size:20px;font-weight:800;color:var(--text);margin-bottom:6px">Submissions</h2>
    <p style="font-size:12px;color:var(--text2)">Customer enquiries from your website forms and supplier referrals. Triage, archive or open in the CRM.</p>
  </div>
  <div class="sub-toolbar" style="display:flex;gap:10px;align-items:center;padding:0 22px 12px;flex-wrap:wrap">
    <input id="sub-search" placeholder="Search name, email, message&hellip;" style="flex:1;min-width:200px;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:8px 12px;color:var(--text);font-size:12px;outline:none" />
    <select id="sub-filter" style="background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:8px 10px;color:var(--text);font-size:12px;outline:none">
      <option value="all">All priorities</option>
      <option value="hot">Hot</option>
      <option value="warm">Warm</option>
      <option value="cold">Cold</option>
    </select>
    <button id="sub-export" style="background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:8px 12px;color:var(--text2);font-size:12px;font-weight:600">Export CSV</button>
    <button id="sub-add" style="background:var(--orange);color:#fff;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700">+ Add</button>
  </div>
  <div id="submissions-list" style="padding:0 22px 24px"></div>
</div>
`;

// ── Modal markup (verbatim port of source kosBuildSubModal, lines 6398–6457)

const DOOR_OPTIONS = [
  "Handleless gloss",
  "Handleless matt",
  "Shaker",
  "In-frame",
  "Slab",
  "Contemporary",
  "Traditional",
  "Bespoke",
  "Not yet decided",
];

const WORKTOP_OPTIONS = [
  "Quartz",
  "Granite",
  "Dekton / porcelain",
  "Solid wood",
  "Laminate",
  "Solid surface",
  "Not yet decided",
];

const BUDGET_OPTIONS = [
  "Under £10,000",
  "£10,000 – £15,000",
  "£15,000 – £25,000",
  "£25,000 – £40,000",
  "£40,000 – £60,000",
  "£60,000+",
];

function fldMarkup(
  id: string,
  label: string,
  ph: string,
  type?: string,
  req?: boolean,
): string {
  return (
    '<div><label style="display:block;font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px">' +
    label +
    (req ? " *" : "") +
    "</label>" +
    '<input id="' +
    id +
    '" type="' +
    (type || "text") +
    '" placeholder="' +
    ph +
    '" ' +
    (req ? "required" : "") +
    ' style="width:100%;background:#1a1a1f;border:1px solid #2a2a2f;border-radius:8px;padding:9px 12px;color:#fff;font-size:13px;box-sizing:border-box;outline:none" onfocus="this.style.borderColor=\'#e58200\'" onblur="this.style.borderColor=\'#2a2a2f\'"></div>'
  );
}

function selMarkup(id: string, label: string, opts: string[]): string {
  const options = opts
    .map((o) => '<option value="' + esc(o) + '">' + esc(o) + "</option>")
    .join("");
  return (
    '<div><label style="display:block;font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px">' +
    label +
    "</label>" +
    '<select id="' +
    id +
    '" style="width:100%;background:#1a1a1f;border:1px solid #2a2a2f;border-radius:8px;padding:9px 12px;color:#fff;font-size:13px;box-sizing:border-box;outline:none;appearance:none;-webkit-appearance:none;background-image:url(\'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23888%22 stroke-width=%222%22><polyline points=%226 9 12 15 18 9%22/></svg>\');background-repeat:no-repeat;background-position:right 10px center;background-size:14px;padding-right:30px" onfocus="this.style.borderColor=\'#e58200\'" onblur="this.style.borderColor=\'#2a2a2f\'"><option value="">Select…</option>' +
    options +
    "</select></div>"
  );
}

function buildModalMarkup(): string {
  return (
    '<div id="kos-sub-overlay" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.72);backdrop-filter:blur(4px);z-index:10020;align-items:center;justify-content:center;padding:16px">' +
    '<div style="background:#111114;border:1px solid #2a2a2f;border-radius:16px;width:100%;max-width:560px;max-height:92vh;overflow:auto;box-shadow:0 24px 80px rgba(0,0,0,.6);animation:kosSubSlide .22s ease-out">' +
    '<div style="padding:20px 24px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #2a2a2f">' +
    '<div style="display:flex;align-items:center;gap:12px">' +
    '<div style="width:36px;height:36px;border-radius:10px;background:rgba(245,158,11,.12);display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#e58200">' + iconMarkup("file", { size: "sm" }) + '</div>' +
    '<div><h3 style="font-size:16px;font-weight:800;color:#fff;margin:0">New Submission</h3><div style="font-size:11px;color:#888;margin-top:2px">Capture a new enquiry</div></div>' +
    "</div>" +
    '<button id="kos-sub-close" style="background:transparent;border:none;color:#888;font-size:22px;cursor:pointer;line-height:1" onmouseover="this.style.color=\'#fff\'" onmouseout="this.style.color=\'#888\'">&times;</button>' +
    "</div>" +
    '<div style="padding:18px 24px 6px">' +
    '<div style="font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.6px;margin-bottom:10px">Contact details</div>' +
    '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">' +
    fldMarkup("kos-sub-name", "Full name", "Customer's full name", "text", true) +
    fldMarkup("kos-sub-phone", "Telephone", "e.g. 07700 900 123", "tel") +
    "</div>" +
    '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px">' +
    fldMarkup("kos-sub-email", "Email", "sarah@example.com", "email") +
    fldMarkup("kos-sub-address", "Address", "42 Elm Road, HG1 1AA") +
    "</div>" +
    "</div>" +
    '<div style="padding:14px 24px 6px">' +
    '<div style="font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.6px;margin-bottom:10px">Kitchen details</div>' +
    '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">' +
    selMarkup("kos-sub-door", "Door style", DOOR_OPTIONS) +
    selMarkup("kos-sub-worktop", "Worktop choice", WORKTOP_OPTIONS) +
    "</div>" +
    '<div style="margin-top:10px">' +
    selMarkup("kos-sub-budget", "Budget bracket", BUDGET_OPTIONS) +
    "</div>" +
    "</div>" +
    '<div style="padding:14px 24px 6px">' +
    '<label style="display:block;font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px">Notes</label>' +
    '<textarea id="kos-sub-notes" rows="3" placeholder="Anything useful -- timescales, inspiration, room size, what they mentioned&hellip;" style="width:100%;background:#1a1a1f;border:1px solid #2a2a2f;border-radius:8px;padding:9px 12px;color:#fff;font-size:13px;box-sizing:border-box;outline:none;resize:vertical;font-family:inherit" onfocus="this.style.borderColor=\'#e58200\'" onblur="this.style.borderColor=\'#2a2a2f\'"></textarea>' +
    "</div>" +
    '<div style="padding:16px 24px 22px;display:flex;gap:8px;justify-content:flex-end">' +
    '<button id="kos-sub-cancel" style="padding:10px 18px;background:transparent;border:1px solid #2a2a2f;color:#aaa;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer">Cancel</button>' +
    '<button id="kos-sub-save" style="padding:10px 24px;background:#e58200;color:#fff;border:none;border-radius:9px;font-size:13px;font-weight:800;cursor:pointer;display:flex;align-items:center;gap:6px" onmouseover="this.style.background=\'#cc7400\'" onmouseout="this.style.background=\'#e58200\'">' +
    iconMarkup("check", { size: "sm" }) + 'Save submission' +
    "</button>" +
    "</div>" +
    "</div>" +
    "</div>"
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const submissionsPage: Page = {
  name: "submissions",
  label: "Submissions",
  icon: "📬",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    container.innerHTML = PAGE_MARKUP;
    const root = container.querySelector<HTMLElement>("#page-submissions")!;

    const listEl = root.querySelector<HTMLElement>("#submissions-list")!;
    const searchInput = root.querySelector<HTMLInputElement>("#sub-search")!;
    const filterSel = root.querySelector<HTMLSelectElement>("#sub-filter")!;
    const exportBtn = root.querySelector<HTMLButtonElement>("#sub-export")!;
    const addBtn = root.querySelector<HTMLButtonElement>("#sub-add")!;
    const toolbarEl = root.querySelector<HTMLElement>(".sub-toolbar")!;

    const overlays = new Set<HTMLElement>();
    const injectedStyleIds = new Set<string>();
    let modalEl: HTMLElement | null = null;
    let disposed = false;

    // Local cache of submissions. With the offline-first layer, the truth
    // lives in IndexedDB (mirrored from PG); we keep this in-memory copy
    // so renders stay synchronous.
    let cache: Submission[] = [];
    let loadState: "loading" | "ready" | "error" = "loading";

    // Offline-first wiring — opened lazily so a typecheck failure here
    // doesn't tank the whole page. `repo`/`syncStatus` are null until ready.
    let db: TkosDb | null = null;
    let repo: Repo<Submission> | null = null;
    let syncStatus: SyncStatusController | null = null;
    const offSubs: Array<() => void> = [];

    const ac = new AbortController();

    // Drain queued ops whenever the browser reports it's back online.
    const onOnline = (): void => {
      if (db && repo) void repo.list();
    };
    window.addEventListener("online", onOnline);

    // ── render (verbatim port of source lines 6333–6360, list-source swapped)
    function renderSubmissions(): void {
      if (disposed) return;
      if (loadState === "loading") {
        listEl.innerHTML =
          '<div style="text-align:center;padding:40px;color:var(--text-muted);font-size:13px">Loading…</div>';
        return;
      }
      if (loadState === "error") {
        listEl.innerHTML =
          '<div style="text-align:center;padding:40px;color:var(--red);font-size:13px">Could not load submissions. Please refresh the page.</div>';
        return;
      }
      const q = (searchInput.value || "").toLowerCase();
      const filt = filterSel.value || "all";
      // The server already excludes archived rows by default, but filter
      // defensively in case the cache mutates locally.
      let subs = cache.filter((s) => !s.archived);
      if (filt !== "all") subs = subs.filter((s) => s.priority === filt);
      if (q) {
        subs = subs.filter(
          (s) =>
            (
              s.name +
              " " +
              s.email +
              " " +
              s.message +
              " " +
              (s.tags || []).join(" ")
            )
              .toLowerCase()
              .indexOf(q) >= 0,
        );
      }
      if (!subs.length) {
        listEl.innerHTML =
          '<div style="text-align:center;padding:40px;color:var(--text-muted);font-size:13px">No submissions match.</div>';
        return;
      }
      listEl.innerHTML = subs
        .sort((a, b) => b.ts - a.ts)
        .map((s) => {
          const pcolMap: Record<Priority, string> = {
            hot: "var(--red)",
            warm: "var(--yellow)",
            cold: "var(--blue)",
          };
          const pcol = pcolMap[s.priority] || "var(--text-muted)";
          const date = new Date(s.ts).toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            '<div class="sub-row" style="background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:14px 16px;margin-bottom:10px;display:flex;gap:14px;align-items:flex-start">' +
            '<div style="width:6px;align-self:stretch;border-radius:3px;background:' +
            pcol +
            '"></div>' +
            '<div style="flex:1;min-width:0">' +
            '<div style="display:flex;justify-content:space-between;gap:10px;margin-bottom:4px"><div style="font-weight:700;color:var(--text);font-size:13px"><span data-customer-name="' +
            esc(s.name) +
            '" data-customer-id="' +
            esc(s.crmContactId || "") +
            '" data-submission-id="' +
            esc(s.id) +
            '" style="cursor:pointer">' +
            esc(s.name) +
            '</span></div><div style="font-size:11px;color:var(--text-muted)">' +
            esc(date) +
            "</div></div>" +
            '<div style="font-size:11px;color:var(--text2);margin-bottom:6px">' +
            esc(maskEmail(s.email)) +
            (s.phone ? " &middot; " + esc(maskPhone(s.phone)) : "") +
            " &middot; " +
            esc(s.source) +
            "</div>" +
            '<div style="font-size:12px;color:var(--text2);line-height:1.5;margin-bottom:8px">' +
            esc(s.message) +
            "</div>" +
            '<div style="display:flex;gap:6px;flex-wrap:wrap">' +
            (s.tags || [])
              .map(
                (t) =>
                  '<span style="background:var(--surface2);border:1px solid var(--border);border-radius:5px;padding:2px 7px;font-size:10px;color:var(--text2)">' +
                  esc(t) +
                  "</span>",
              )
              .join("") +
            "</div>" +
            "</div>" +
            '<div style="display:flex;flex-direction:column;gap:5px">' +
            '<button class="sa-btn" data-action="open-crm" data-id="' +
            esc(s.id) +
            '">Open in CRM</button>' +
            '<button class="sa-btn" data-action="reply" data-id="' +
            esc(s.id) +
            '">Reply</button>' +
            '<button class="sa-btn" data-action="archive" data-id="' +
            esc(s.id) +
            '">Archive</button>' +
            '<button class="sa-btn" data-action="delete" data-id="' +
            esc(s.id) +
            '" style="color:var(--red)">Delete</button>' +
            "</div>" +
            "</div>"
          );
        })
        .join("");
    }

    // ── Backend (offline-first repo) ────────────────────────────────────────

    async function refreshCacheFromRepo(): Promise<void> {
      if (!repo) return;
      cache = await repo.cached();
      // Sort newest-first for the list render; CSV export does its own thing.
      cache.sort((a, b) => b.ts - a.ts);

      // Publish submissions into the shared mini-CRM cache, but ONLY when
      // the submission is already linked to a real CRM contact. Seeding the
      // cache with synthetic records keyed by submission id ("sub-…")
      // poisoned the Mini-CRM "Full record" navigation — the customer page
      // would receive the submission id and render "isn't in your CRM".
      //
      // Unlinked submissions fall through to the Mini-CRM popup's
      // not-found path, which now offers to create / link a CRM contact
      // via POST /api/portal/submissions/:id/link-crm.
      const customers: MiniCrmCustomer[] = cache
        .filter((s) => !s.archived && s.name && s.crmContactId)
        .map((s) => {
          const m: MiniCrmCustomer = {
            id:      s.crmContactId!,
            name:    s.name,
            focusId: s.crmContactId!,
            stage:   "Lead",
          };
          if (s.email)   m.email = s.email;
          if (s.phone)   m.phone = s.phone;
          if (s.message) m.nextAction = s.message.slice(0, 80);
          return m;
        });
      if (customers.length > 0) customerCache.merge(customers);
    }

    async function loadSubmissions(): Promise<void> {
      if (!repo) return;
      try {
        await repo.list();
        if (disposed) return;
        await refreshCacheFromRepo();
        loadState = "ready";
        renderSubmissions();
      } catch (err) {
        if (isAbortError(err)) return;
        if (disposed) return;
        console.error("[submissions] load failed", err);
        // Even on error, the repo's `list()` returns the IDB cache; if cache
        // is non-empty, show it. Only true unrecoverable error if nothing.
        await refreshCacheFromRepo();
        loadState = cache.length > 0 ? "ready" : "error";
        renderSubmissions();
      }
    }

    // ── Action handlers (port of source lines 6361–6364, repo-backed) ──────
    // Open the customer's CRM record directly. A wizard/website submission is
    // already a CRM lead, so there's no "conversion" step — we just jump to
    // the record. Linked submissions carry `crmContactId`; unlinked ones are
    // find-or-created via /link-crm first (mirrors the mini-CRM popup).
    async function openInCrm(id: string): Promise<void> {
      const s = cache.find((x) => x.id === id);
      if (!s) return;
      if (s.crmContactId) {
        ctx.navigate("customer", { id: s.crmContactId });
        return;
      }
      try {
        interface LinkResp { ok?: boolean; contact?: { id?: string } }
        const resp = await ctx.api<LinkResp>(
          `/portal/submissions/${encodeURIComponent(id)}/link-crm`,
          { method: "POST", body: JSON.stringify({}) },
        );
        const cid = resp?.contact?.id;
        if (cid) {
          ctx.navigate("customer", { id: cid });
        } else {
          ctx.showToast("Couldn't open this customer's record.", "error");
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[submissions] open-in-crm failed", err);
        ctx.showToast("Couldn't open this customer's record.", "error");
      }
    }

    async function replySubmission(id: string): Promise<void> {
      if (!repo) return;
      const s = cache.find((x) => x.id === id);
      if (!s) return;
      try {
        await repo.update(id, { status: "replied" });
        if (disposed) return;
        await refreshCacheFromRepo();
        ctx.showToast("Reply sent to " + s.email + " (demo)");
        renderSubmissions();
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[submissions] reply failed", err);
        ctx.showToast("Could not send reply", "error");
      }
    }

    async function archiveSubmission(id: string): Promise<void> {
      if (!repo) return;
      const s = cache.find((x) => x.id === id);
      if (!s) return;
      try {
        await repo.update(id, { archived: true });
        if (disposed) return;
        await refreshCacheFromRepo();
        ctx.showToast("Submission archived");
        renderSubmissions();
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[submissions] archive failed", err);
        ctx.showToast("Could not archive submission", "error");
      }
    }

    async function deleteSubmission(id: string): Promise<void> {
      if (!repo) return;
      if (!(await confirmDialog("Delete this submission?", {
        title: "Delete submission",
        confirmLabel: "Delete",
        destructive: true,
      }))) return;
      try {
        await repo.remove(id);
        if (disposed) return;
        await refreshCacheFromRepo();
        ctx.showToast("Submission deleted");
        renderSubmissions();
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[submissions] delete failed", err);
        ctx.showToast("Could not delete submission", "error");
      }
    }

    // ── Modal lifecycle (verbatim port of source lines 6366–6396) ──────────
    function buildModal(): HTMLElement {
      // Inject one-off animation keyframes (source line 6451–6455).
      if (!document.getElementById("kos-sub-style")) {
        const st = document.createElement("style");
        st.id = "kos-sub-style";
        st.textContent =
          "@keyframes kosSubSlide{from{opacity:0;transform:translateY(-20px)}to{opacity:1;transform:translateY(0)}}";
        document.head.appendChild(st);
        injectedStyleIds.add("kos-sub-style");
      }
      const w = document.createElement("div");
      w.innerHTML = buildModalMarkup();
      const overlay = w.firstChild as HTMLElement;
      document.body.appendChild(overlay);
      overlays.add(overlay);

      // Wire close handlers.
      overlay.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).id === "kos-sub-overlay") closeModal();
      });
      overlay
        .querySelector<HTMLButtonElement>("#kos-sub-close")
        ?.addEventListener("click", closeModal);
      overlay
        .querySelector<HTMLButtonElement>("#kos-sub-cancel")
        ?.addEventListener("click", closeModal);
      overlay
        .querySelector<HTMLButtonElement>("#kos-sub-save")
        ?.addEventListener("click", () => { void submitModal(); });
      return overlay;
    }

    function openModal(): void {
      if (!modalEl) modalEl = buildModal();
      const ids = [
        "kos-sub-name",
        "kos-sub-phone",
        "kos-sub-email",
        "kos-sub-address",
        "kos-sub-door",
        "kos-sub-worktop",
        "kos-sub-budget",
        "kos-sub-notes",
      ];
      ids.forEach((id) => {
        const el = modalEl?.querySelector<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >("#" + id);
        if (el) el.value = "";
      });
      modalEl.style.display = "flex";
      setTimeout(() => {
        const f = modalEl?.querySelector<HTMLInputElement>("#kos-sub-name");
        if (f) f.focus();
      }, 80);
    }

    function closeModal(): void {
      if (modalEl) modalEl.style.display = "none";
    }

    function readField(id: string): string {
      const el = modalEl?.querySelector<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >("#" + id);
      return (el?.value || "").trim();
    }

    async function submitModal(): Promise<void> {
      if (!repo) return;
      const name = readField("kos-sub-name");
      if (!name) {
        modalEl?.querySelector<HTMLInputElement>("#kos-sub-name")?.focus();
        return;
      }
      const phone = readField("kos-sub-phone");
      const email = readField("kos-sub-email");
      const address = readField("kos-sub-address");
      const door = readField("kos-sub-door");
      const worktop = readField("kos-sub-worktop");
      const budget = readField("kos-sub-budget");
      const notes = readField("kos-sub-notes");

      const bits: string[] = [];
      if (door) bits.push("Door style: " + door);
      if (worktop) bits.push("Worktop: " + worktop);
      if (budget) bits.push("Budget: " + budget);
      if (address) bits.push("Address: " + address);
      if (notes) bits.push("Notes: " + notes);
      const msg = bits.join("\n");

      // Generate a client-side id so the repo's optimistic write has a
      // stable key. The server will rewrite it on successful POST and the
      // repo will reconcile the cache.
      const localId =
        "sub-local-" +
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 8);

      const record: Submission = {
        id: localId,
        ts: Date.now(),
        name,
        email,
        phone,
        message: msg,
        source: "Manual entry",
        priority: "warm",
        status: "new",
        archived: false,
        tags: [],
      };

      const saveBtn = modalEl?.querySelector<HTMLButtonElement>("#kos-sub-save");
      if (saveBtn) saveBtn.disabled = true;
      try {
        await repo.create(record);
        if (disposed) return;
        await refreshCacheFromRepo();
        closeModal();
        renderSubmissions();
        ctx.showToast("Submission added for " + name, "success");
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[submissions] create failed", err);
        ctx.showToast("Could not save submission", "error");
      } finally {
        if (saveBtn) saveBtn.disabled = false;
      }
    }

    // ── CSV export (verbatim port of source line 6458, reads in-memory cache)
    function exportSubmissionsCsv(): void {
      const rows: string[][] = [
        ["Date", "Name", "Email", "Phone", "Source", "Priority", "Status", "Message"],
      ];
      cache.forEach((s) => {
        rows.push([
          new Date(s.ts).toISOString(),
          s.name,
          s.email,
          s.phone,
          s.source,
          s.priority,
          s.status,
          (s.message || "").replace(/\n/g, " "),
        ]);
      });
      const csv = rows
        .map((r) =>
          r
            .map((c) => '"' + String(c == null ? "" : c).replace(/"/g, '""') + '"')
            .join(","),
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        "submissions-" + new Date().toISOString().slice(0, 10) + ".csv";
      a.click();
      URL.revokeObjectURL(url);
      ctx.showToast("CSV downloaded");
    }

    // ── Event wiring (replaces source's inline on* attrs) ───────────────────
    searchInput.addEventListener("input", renderSubmissions);
    filterSel.addEventListener("change", renderSubmissions);
    exportBtn.addEventListener("click", exportSubmissionsCsv);
    addBtn.addEventListener("click", openModal);

    // Delegated action buttons on each rendered card.
    listEl.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest<HTMLButtonElement>("button[data-action]");
      if (!btn) return;
      const action = btn.dataset["action"];
      const id = btn.dataset["id"];
      if (!action || !id) return;
      if (action === "open-crm") void openInCrm(id);
      else if (action === "reply") void replySubmission(id);
      else if (action === "archive") void archiveSubmission(id);
      else if (action === "delete") void deleteSubmission(id);
    });

    // ── Offline-first init ──────────────────────────────────────────────────
    // Open IndexedDB, build the repo + sync status, mount the sync dot in
    // the toolbar, then kick off the initial load.
    async function initOffline(): Promise<void> {
      try {
        db = await openDb(["submissions", "_sync_queue"]);
        if (disposed) return;
        const queue = createSyncQueue(db);
        syncStatus = createSyncStatus(queue);
        repo = createRepo<Submission>({
          store: "submissions",
          endpoint: "/portal/submissions",
          api: rawApi,
          db,
          queue,
          parseList,
          parseOne,
        });

        // Re-render on any cache change (e.g. a queue drain finally lands a
        // server response while the user is on another tab).
        offSubs.push(
          repo.on("change", () => {
            void (async (): Promise<void> => {
              if (disposed || !repo) return;
              await refreshCacheFromRepo();
              if (loadState === "ready") renderSubmissions();
            })();
          }),
        );

        // Server-wins conflict — toast the user and re-render.
        offSubs.push(
          repo.on("conflict", (...args: unknown[]) => {
            const payload = args[0] as
              | RepoConflictPayload<Submission>
              | undefined;
            const sv = payload?.serverVersion;
            const who = sv?.name ?? "Submission";
            ctx.showToast(
              who +
                " updated on another device — server version applied",
              "info",
            );
          }),
        );

        // Insert the sync dot into the toolbar, just before the "+ Add"
        // button. This keeps it visible next to the primary action and
        // doesn't disturb the existing flex layout.
        const dot = createSyncDot({ status: syncStatus });
        dot.el.style.marginLeft = "4px";
        dot.el.style.marginRight = "4px";
        toolbarEl.insertBefore(dot.el, addBtn);
        offSubs.push(() => dot.destroy());

        // Drive the syncing-dot while a list refresh runs.
        const wrappedList = (async (): Promise<void> => {
          syncStatus?.setDraining(true);
          try {
            await loadSubmissions();
          } finally {
            syncStatus?.setDraining(false);
          }
        });
        void wrappedList();
      } catch (err) {
        console.error("[submissions] offline init failed", err);
        // Fail closed → show the same error state the page used to show on
        // a load error. The user is not stuck; refresh recovers.
        loadState = "error";
        renderSubmissions();
      }
    }

    // Initial render (shows the loading placeholder) and kick off the fetch.
    renderSubmissions();
    void initOffline();

    return (): void => {
      disposed = true;
      ac.abort();
      window.removeEventListener("online", onOnline);
      for (const off of offSubs) {
        try {
          off();
        } catch (err) {
          console.error("[submissions] cleanup listener threw", err);
        }
      }
      offSubs.length = 0;
      syncStatus?.destroy();
      syncStatus = null;
      repo = null;
      db = null;
      for (const ov of overlays) ov.remove();
      overlays.clear();
      modalEl = null;
      for (const id of injectedStyleIds) {
        document.getElementById(id)?.remove();
      }
      injectedStyleIds.clear();
    };
  },
};

registerPage(submissionsPage);

export default submissionsPage;
